import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SITE } from "@/lib/site";

const primaryLinks = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/service-areas", label: "Service Areas" },
];

const resourcesLinks = [
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
];

const mobileLinks = [
  ...primaryLinks,
  ...resourcesLinks,
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const resourcesActive = resourcesLinks.some((r) =>
    location.pathname.startsWith(r.to)
  );

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-md border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-2 group"
            onClick={() => setOpen(false)}
          >
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-md bg-blue-700 text-white font-display font-black text-lg leading-none">
              9
            </span>
            <span className="font-display font-black tracking-tight text-slate-900 text-xl">
              901 Lots
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {primaryLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? "text-blue-700 bg-blue-50"
                      : "text-slate-700 hover:text-blue-700 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}

            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="nav-resources-trigger"
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 outline-none ${
                  resourcesActive
                    ? "text-blue-700 bg-blue-50"
                    : "text-slate-700 hover:text-blue-700 hover:bg-slate-50"
                }`}
              >
                Resources
                <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {resourcesLinks.map((r) => (
                  <DropdownMenuItem key={r.to} asChild>
                    <Link
                      to={r.to}
                      data-testid={`nav-resources-${r.label.toLowerCase()}`}
                      className="cursor-pointer"
                    >
                      {r.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <NavLink
              to="/about"
              data-testid="nav-link-about"
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? "text-blue-700 bg-blue-50"
                    : "text-slate-700 hover:text-blue-700 hover:bg-slate-50"
                }`
              }
            >
              About
            </NavLink>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={SITE.phoneHref}
              data-testid="navbar-phone"
              className="hidden lg:inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-blue-700"
            >
              <Phone className="h-4 w-4" />
              {SITE.phone}
            </a>
            <Button
              asChild
              data-testid="navbar-quote-cta"
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-md px-5"
            >
              <Link to="/quote">Free Quote</Link>
            </Button>
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="navbar-menu-toggle"
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-slate-200 text-slate-700"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-3 space-y-1">
            {mobileLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-md text-base font-medium ${
                    isActive
                      ? "text-blue-700 bg-blue-50"
                      : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/quote"
              onClick={() => setOpen(false)}
              data-testid="mobile-quote-cta"
              className="block w-full text-center mt-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-md px-5 py-3"
            >
              Free Quote
            </Link>
            <a
              href={SITE.phoneHref}
              className="block px-3 py-3 text-slate-700 text-sm border-t border-slate-100 mt-2"
              data-testid="mobile-phone"
            >
              <Phone className="inline h-4 w-4 mr-2" />
              {SITE.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
